import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type ApiError, apiFetch } from '@/lib/api/client';
import { useToast } from '@/components/ui/toast';
import { DEALS_QUERY_KEY, patchDealInCaches, restoreDealItem, snapshotDealItem } from './useDeals';
import { type ApiDealResponse, readApiETag } from './types';

interface PauseArgs {
  id: string;
  /** ETag (RowVersion base64) do item, para o If-Match. */
  etag: string;
}

/**
 * Coloca um deal em pausa via POST /api/deals/{id}/pause com If-Match (ETag). Sem comentário
 * (pausa é leve/reversível — item 4). Otimista: marca auxiliaryStatus 'Paused' no cache por prefixo;
 * 412 → rollback + refetch + aviso. Como a transição muda a "coluna" de status, invalida no sucesso
 * (reconcilia totais e a página de destino — espelha Discard, DEC-038).
 */
export function usePauseDeal() {
  const qc = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: ({ id, etag }: PauseArgs) =>
      apiFetch<ApiDealResponse>(`/api/deals/${id}/pause`, {
        method: 'POST',
        headers: { 'If-Match': `"${etag}"` },
      }),
    onMutate: async ({ id }) => {
      await qc.cancelQueries({ queryKey: DEALS_QUERY_KEY });
      const prev = snapshotDealItem(qc, id);
      patchDealInCaches(qc, id, (it) => ({ ...it, auxiliaryStatus: 'Paused' }));
      return { prev };
    },
    onError: (err, vars, ctx) => {
      if (ctx?.prev) restoreDealItem(qc, vars.id, ctx.prev);
      if ((err as ApiError).status === 412) {
        toast('Esse deal foi alterado por outra pessoa. Atualizei a lista.', 'warn');
        void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      } else {
        toast('Não foi possível pausar o deal. Tente de novo.', 'danger');
      }
    },
    onSuccess: (resp) => {
      patchDealInCaches(qc, resp.id, (it) => ({
        ...it,
        auxiliaryStatus: resp.auxiliaryStatus,
        eTag: readApiETag(resp) || it.eTag,
      }));
      void qc.invalidateQueries({ queryKey: DEALS_QUERY_KEY });
      toast('Deal colocado em pausa.');
    },
  });
}
